import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Star rating component
const StarRating = ({ rating, reviewCount = 0 }) => {
  const displayRating = rating ? rating.toFixed(1) : '0.0';
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-light-text-secondary/20 dark:text-silver/20'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-sm text-light-text-secondary dark:text-silver/60 ml-1">
        {displayRating} ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
};

// Doctor card component
const DoctorCard = ({ doctor }) => {
  // Format doctor name from firstName/lastName
  const doctorName = `Dr. ${doctor.firstName} ${doctor.lastName}`;

  // Parse specialties - handle both array and JSON string formats
  let specialtiesArray = [];
  if (doctor.specialties) {
    if (Array.isArray(doctor.specialties)) {
      specialtiesArray = doctor.specialties;
    } else if (typeof doctor.specialties === 'string') {
      try {
        const parsed = JSON.parse(doctor.specialties);
        specialtiesArray = Array.isArray(parsed) ? parsed : [];
      } catch {
        specialtiesArray = [];
      }
    }
  }

  // Get primary specialty or default
  const primarySpecialty = specialtiesArray.length > 0
    ? specialtiesArray[0]
    : 'General Practice';

  return (
    <div className="card hover:border-light-border dark:hover:border-silver/20 transition-smooth group">
      <div className="flex gap-4">
        {/* Doctor Photo */}
        <div className="w-20 h-20 rounded-xl bg-light-bg-secondary dark:bg-silver/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {doctor.photoUrl ? (
            <img src={doctor.photoUrl} alt={doctorName} className="w-full h-full object-cover" />
          ) : (
            <svg className="w-10 h-10 text-light-text-secondary/30 dark:text-silver/30" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>

        {/* Doctor Info */}
        <div className="flex-1 min-w-0">
          <h3
            className="font-display text-xl italic text-light-text dark:text-pure-white group-hover:text-light-text-secondary dark:group-hover:text-silver transition-smooth truncate"
            title={doctorName}
          >
            {doctorName}
          </h3>
          <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mt-1">
            {primarySpecialty}
          </p>
          {doctor.bio && (
            <p className="text-sm text-light-text-secondary dark:text-silver/60 mt-2 line-clamp-2">
              {doctor.bio}
            </p>
          )}
        </div>
      </div>

      {/* Rating */}
      <div className="mt-4">
        <StarRating rating={doctor.averageRating} reviewCount={doctor.reviewCount} />
      </div>

      {/* Specialties (if multiple) */}
      {specialtiesArray.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {specialtiesArray.slice(0, 3).map((specialty, idx) => (
            <span key={idx} className="text-xs px-2 py-1 rounded bg-light-bg-secondary dark:bg-silver/10 text-light-text-secondary dark:text-silver/70">
              {specialty}
            </span>
          ))}
        </div>
      )}

      {/* Book Button */}
      <Link
        to={`/doctor/${doctor.id}`}
        className="btn-primary w-full mt-4 text-center block"
      >
        View Profile
      </Link>
    </div>
  );
};

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Initialize state from URL params for persistence across navigation
  // Trim whitespace - whitespace-only search should be treated as empty
  const searchQuery = (searchParams.get('search') || '').trim();
  const selectedSpecialty = searchParams.get('specialty') || '';
  const selectedLocation = (searchParams.get('location') || '').trim();
  const maxPrice = searchParams.get('maxPrice') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  // Update URL when filters change (resets to page 1)
  const updateFilters = (newSearch, newSpecialty, newLocation, newMaxPrice) => {
    const params = new URLSearchParams();
    // Trim whitespace - whitespace-only search should be treated as empty
    const trimmedSearch = newSearch ? newSearch.trim() : '';
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }
    if (newSpecialty && newSpecialty !== 'All Specialties') {
      params.set('specialty', newSpecialty);
    }
    const trimmedLocation = newLocation ? newLocation.trim() : '';
    if (trimmedLocation) {
      params.set('location', trimmedLocation);
    }
    if (newMaxPrice && parseFloat(newMaxPrice) > 0) {
      params.set('maxPrice', newMaxPrice);
    }
    // Reset to page 1 when filters change
    setSearchParams(params, { replace: true });
  };

  // Navigate to a specific page
  const goToPage = (pageNum) => {
    const params = new URLSearchParams(searchParams);
    if (pageNum > 1) {
      params.set('page', pageNum.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params, { replace: true });
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery ||
    (selectedSpecialty && selectedSpecialty !== 'All Specialties') ||
    selectedLocation ||
    (maxPrice && parseFloat(maxPrice) > 0);

  // Clear all filters
  const clearFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const specialties = [
    'All Specialties',
    'General Practice',
    'Cardiology',
    'Dentistry',
    'Dermatology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Family Medicine',
    'Preventive Care',
  ];

  // Fetch doctors from API
  useEffect(() => {
    const fetchDoctors = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (searchQuery) {
          params.append('search', searchQuery);
        }
        if (selectedSpecialty && selectedSpecialty !== 'All Specialties') {
          params.append('specialty', selectedSpecialty);
        }
        if (selectedLocation) {
          params.append('location', selectedLocation);
        }
        if (maxPrice && parseFloat(maxPrice) > 0) {
          params.append('maxPrice', maxPrice);
        }
        // Add pagination params
        params.append('page', currentPage.toString());
        params.append('limit', '12'); // 12 doctors per page

        const url = `${API_BASE}/doctors${params.toString() ? '?' + params.toString() : ''}`;
        console.log('[Marketplace] Fetching doctors from:', url);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch doctors: ${response.status}`);
        }

        const data = await response.json();
        console.log('[Marketplace] Received doctors:', data);
        setDoctors(data.doctors || []);
        setPagination({
          page: data.page || 1,
          totalPages: data.totalPages || 1,
          total: data.total || 0,
          hasNextPage: data.hasNextPage || false,
          hasPrevPage: data.hasPrevPage || false
        });
      } catch (err) {
        console.error('[Marketplace] Error fetching doctors:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search queries
    const timeoutId = setTimeout(fetchDoctors, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedSpecialty, selectedLocation, maxPrice, currentPage]);

  // Pagination helper to generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const { page, totalPages } = pagination;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (page <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-8 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl italic tracking-tighter text-light-text dark:text-pure-white mb-4">
            Find Your Doctor
          </h1>
          <p className="font-body text-lg text-light-text-secondary dark:text-silver/60 max-w-2xl">
            Discover verified healthcare professionals and book your appointment today.
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* First row: Search and Specialty */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-secondary/40 dark:text-silver/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                id="doctor-search"
                placeholder="Search by name or specialty..."
                value={searchQuery}
                onChange={(e) => updateFilters(e.target.value, selectedSpecialty, selectedLocation, maxPrice)}
                className="input-field pl-12"
                aria-label="Search doctors by name or specialty"
              />
            </div>

            {/* Specialty Filter */}
            <select
              id="specialty-filter"
              value={selectedSpecialty || 'All Specialties'}
              onChange={(e) => updateFilters(searchQuery, e.target.value, selectedLocation, maxPrice)}
              className="input-field md:w-64 cursor-pointer"
              aria-label="Filter by specialty"
            >
              {specialties.map((specialty) => (
                <option key={specialty} value={specialty} className="bg-light-bg dark:bg-obsidian text-light-text dark:text-silver">
                  {specialty}
                </option>
              ))}
            </select>
          </div>

          {/* Second row: Location, Max Price, and Clear Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Location Filter */}
            <div className="relative flex-1">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-secondary/40 dark:text-silver/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <input
                type="text"
                id="location-filter"
                placeholder="Filter by location..."
                value={selectedLocation}
                onChange={(e) => updateFilters(searchQuery, selectedSpecialty, e.target.value, maxPrice)}
                className="input-field pl-12"
                aria-label="Filter by location"
              />
            </div>

            {/* Max Price Filter */}
            <div className="relative md:w-48">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-light-text-secondary/40 dark:text-silver/40 font-mono" aria-hidden="true">£</span>
              <input
                type="number"
                id="max-price-filter"
                placeholder="Max price"
                min="0"
                step="1"
                value={maxPrice}
                onChange={(e) => updateFilters(searchQuery, selectedSpecialty, selectedLocation, e.target.value)}
                className="input-field pl-8"
                aria-label="Maximum price in GBP"
              />
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-3 rounded-xl font-mono text-xs uppercase tracking-widest border border-light-border dark:border-border text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver hover:border-light-text/20 dark:hover:border-silver/30 transition-smooth flex items-center gap-2 whitespace-nowrap"
                aria-label="Clear all filters"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-light-text-secondary/20 dark:border-silver/20 border-t-light-text dark:border-t-silver rounded-full animate-spin mb-4"></div>
              <p className="text-light-text-secondary dark:text-silver/60 font-mono text-sm uppercase tracking-widest">
                Loading doctors...
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-16">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 max-w-md mx-auto">
                <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="font-display text-xl italic text-red-400 mb-2">
                  Failed to load doctors
                </h3>
                <p className="text-light-text-secondary dark:text-silver/60 text-sm mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Results (only show when not loading and no error) */}
          {!loading && !error && (
            <>
              {/* Results Count with Pagination Info */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary/60 dark:text-silver/40">
                  {pagination.total} Doctor{pagination.total !== 1 ? 's' : ''} Found
                </p>
                {pagination.totalPages > 1 && (
                  <p className="font-mono text-xs text-light-text-secondary/60 dark:text-silver/40">
                    Page {pagination.page} of {pagination.totalPages}
                  </p>
                )}
              </div>

              {/* Doctor Cards Grid */}
              {doctors.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {doctors.map((doctor) => (
                      <DoctorCard key={doctor.id} doctor={doctor} />
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {pagination.totalPages > 1 && (
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-12 px-2 max-w-full overflow-hidden">
                      {/* Previous Button */}
                      <button
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg font-mono text-sm transition-smooth ${
                          pagination.hasPrevPage
                            ? 'bg-light-bg-secondary dark:bg-silver/10 text-light-text dark:text-silver hover:bg-light-bg-tertiary dark:hover:bg-silver/20 cursor-pointer'
                            : 'bg-light-bg-secondary/50 dark:bg-silver/5 text-light-text-secondary/30 dark:text-silver/30 cursor-not-allowed'
                        }`}
                        aria-label="Previous page"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="hidden sm:inline">Prev</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1 flex-wrap justify-center">
                        {getPageNumbers().map((pageNum, idx) => (
                          pageNum === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-1 sm:px-2 text-light-text-secondary/40 dark:text-silver/40">...</span>
                          ) : (
                            <button
                              key={pageNum}
                              onClick={() => goToPage(pageNum)}
                              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-mono text-sm transition-smooth ${
                                pageNum === pagination.page
                                  ? 'bg-light-text dark:bg-silver text-light-bg dark:text-obsidian'
                                  : 'bg-light-bg-secondary dark:bg-silver/10 text-light-text dark:text-silver hover:bg-light-bg-tertiary dark:hover:bg-silver/20'
                              }`}
                              aria-label={`Go to page ${pageNum}`}
                              aria-current={pageNum === pagination.page ? 'page' : undefined}
                            >
                              {pageNum}
                            </button>
                          )
                        ))}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={!pagination.hasNextPage}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg font-mono text-sm transition-smooth ${
                          pagination.hasNextPage
                            ? 'bg-light-bg-secondary dark:bg-silver/10 text-light-text dark:text-silver hover:bg-light-bg-tertiary dark:hover:bg-silver/20 cursor-pointer'
                            : 'bg-light-bg-secondary/50 dark:bg-silver/5 text-light-text-secondary/30 dark:text-silver/30 cursor-not-allowed'
                        }`}
                        aria-label="Next page"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <svg
                    className="w-16 h-16 text-light-text-secondary/20 dark:text-silver/20 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="font-display text-xl italic text-light-text-secondary dark:text-silver/60 mb-2">
                    No doctors found
                  </h3>
                  <p className="text-light-text-secondary/60 dark:text-silver/40">
                    Try adjusting your search or filters
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Marketplace;
